import React, { createContext, useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "./axios";
import { useIdleTimer } from "react-idle-timer";

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // FONCTIONS UTILITAIRES (définies avant les states qui les utilisent)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const getCookie = useCallback((userInfo) => {
    let user = userInfo + "=";
    let decodeCookie = decodeURIComponent(document.cookie);
    let cookies = decodeCookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      let c = cookies[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(user) === 0) {
        const dataStringReturned = c.substring(user.length, c.length);
        try {
          return JSON.parse(decodeURIComponent(dataStringReturned));
        } catch (e) {
          console.error("Error parsing cookie:", e);
          return null;
        }
      }
    }
    return null;
  }, []);

  const setCookie = useCallback((userInfo, userInfoValues) => {
    const nowDate = new Date();
    nowDate.setTime(nowDate.getTime() + 24 * 60 * 60 * 1000);
    let expires = "expires=" + nowDate.toUTCString();
    const dataString = JSON.stringify(userInfoValues);
    document.cookie =
      userInfo +
      "=" +
      encodeURIComponent(dataString) +
      ";" +
      expires +
      ";path=/";
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // ÉTATS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const [Users, setUsers] = useState(null);
  const [token, _setToken] = useState(localStorage.getItem("access_token"));
  const [loggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  // ✅ FIX CRITIQUE: Initialiser USE depuis le cookie dès le départ
  const [USE, setUSE] = useState(() => {
    const userFromCookie = getCookie("userInfo");
    console.log("🔵 Initial USE from cookie:", userFromCookie);
    return userFromCookie || {};
  });

  const [formValue, setformValue] = useState({
    name: "",
    password: "",
  });

  const [notification, setNotification] = useState({
    message: "Nouvelle Tache Pour",
    TechnicianName: "",
    status: "",
  });

  const [notificationCompta, setNotificationCompta] = useState({
    status: "",
    message: [],
  });

  const [counter, setCounter] = useState(0);
  const [isLogicDone, setIsLogicDone] = useState(false);

  const navigate = useNavigate();

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setformValue((prev) => ({ ...prev, [name]: value }));
  }, []);

  const redirectUser = useCallback((user) => {
    let userInformation;
    
    if (getCookie("userInfo") != null) {
      userInformation = getCookie("userInfo");
      setUSE(userInformation);
    } else {
      userInformation = user;
    }

    console.log("🔵 Redirecting user with profil_id:", userInformation?.profil_id);

    if (!userInformation) {
      navigate("/");
      return;
    }

    // Redirection selon profil_id
    switch (userInformation.profil_id) {
      case 5:  // Admin
        navigate("/utilisateurs");
        break;
      case 4:  // Commercial
        navigate("/commercial/tâches");
        break;
      case 1:  // Technicien
        navigate("/technicien/tâches");
        break;
      case 2:  // Gérant
      case 3:  // Support Technique
      case 28: // Comptable
        navigate("/Dashboard");
        break;
      default:
        navigate("/");
    }
  }, [getCookie, navigate]);

  const setToken = useCallback(async (token) => {
    _setToken(token);
    if (token) {
      try {
        localStorage.setItem("access_token", token);
        console.log("✅ Token saved to localStorage");
      } catch (error) {
        console.error("❌ Error saving token:", error);
      }
    } else {
      localStorage.removeItem("access_token");
      navigate("/");
    }
  }, [navigate]);

  const checkUserLoggedIn = useCallback(async () => {
    try {
      const response = await axios.get("api/checkLoggedIn");
      setLoggedIn(response.data.loggedIn);
      
      if (response.data.loggedIn != true) {
        // Récupérer l'utilisateur depuis le cookie
      //   const userFromCookie = getCookie("userInfo");
      //   if (userFromCookie) {
      //     console.log("🟢 User logged in, USE set from cookie:", userFromCookie);
      //     setUSE(userFromCookie);
      //   }
      //   redirectUser();
        setToken(null);
      } 
      // else {
      //   setToken(null);
      // }
    } catch (error) {
      console.error("❌ Error checking login:", error);
      setToken(null);
    }
  }, [getCookie, redirectUser, setToken]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.get("/sanctum/csrf-cookie");
      const res = await axios.post("/login", formValue);

      const userInfo = res.data.user;
      console.log("🟢 Login successful, user:", userInfo);

      // Sauvegarder dans le state ET le cookie
      setUSE(userInfo);
      setCookie("userInfo", userInfo);
      await setToken(res.data.token);

      // Redirection selon profil_id
      switch (userInfo.profil_id) {
        case 5:
          navigate("/utilisateurs");
          break;
        case 4:
          navigate("/commercial/tâches");
          break;
        case 1:
          navigate("/technicien/tâches");
          break;
        case 2:
        case 3:
        case 28:
          navigate("/Dashboard");
          break;
        default:
          navigate("/");
      }
    } catch (err) {
      console.error("❌ Login error:", err);
      setError("Invalid Username or Password");
    } finally {
      setLoading(false);
    }
  }, [formValue, setCookie, setToken, navigate]);

  const handleLogOut = useCallback(async () => {
    try {
      await axios.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setToken(null);
      setUSE({});
      // Supprimer le cookie
      document.cookie = "userInfo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      navigate("/");
    }
  }, [setToken, navigate]);

  const getUser = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/user");
      return data;
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  }, []);

  const getCOmptaAppareil = useCallback(async () => {
    try {
      const res = await axios.get("/api/comptable/allAppareils");
      
      let newCounter = 0;
      const messages = [];

      if (res.data.NumberGps < 10) {
        newCounter++;
        messages.push("GPS Almost Done");
      }
      if (res.data.NumberSIM < 10) {
        newCounter++;
        messages.push("SIM Almost Done");
      }
      if (res.data.NumberAccessory < 10) {
        newCounter++;
        messages.push("ACC Almost Done");
      }

      setCounter(newCounter);
      setNotificationCompta({
        status: newCounter,
        message: messages,
      });
      setIsLogicDone((prev) => !prev);
    } catch (error) {
      console.error("Error fetching comptable appareils:", error);
    }
  }, []);

  const updatedTheStatus = useCallback(() => {
    setNotificationCompta((prevState) => ({
      ...prevState,
      status: counter,
    }));
  }, [counter]);

  // ═══════════════════════════════════════════════════════════════════════════
  // IDLE TIMER
  // ═══════════════════════════════════════════════════════════════════════════
  
  const handleOnIdle = useCallback(() => {
    _setToken(null);
    localStorage.removeItem("access_token");
    document.cookie = "userInfo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/");
  }, [navigate]);

  const handleOnAction = useCallback(() => {}, []);

  useIdleTimer({
    timeout: 1000 * 60 * 60, // 1 heure
    onIdle: handleOnIdle,
    onAction: handleOnAction,
  });

  // Auto-logout après 12 heures
  useEffect(() => {
    const timeout = setTimeout(() => {
      handleOnIdle();
    }, 43200000); // 12 heures

    return () => clearTimeout(timeout);
  }, [handleOnIdle]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONTEXT VALUE (Optimisé avec useMemo)
  // ═══════════════════════════════════════════════════════════════════════════
  
  const value = useMemo(
    () => ({
      Users,
      handleLogin,
      handleChange,
      error,
      formValue,
      loading,
      USE,
      handleLogOut,
      getUser,
      setNotification,
      notification,
      setNotificationCompta,
      updatedTheStatus,
      getCOmptaAppareil,
      isLogicDone,
      setCounter,
      notificationCompta,
      token,
      handleOnIdle,
      checkUserLoggedIn,
    }),
    [
      Users,
      handleLogin,
      handleChange,
      error,
      formValue,
      loading,
      USE,
      handleLogOut,
      getUser,
      notification,
      updatedTheStatus,
      getCOmptaAppareil,
      isLogicDone,
      notificationCompta,
      token,
      handleOnIdle,
      checkUserLoggedIn,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;